import { inject, Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ECampaignStatus, EDeliverableType, EPlatformFeature } from '@enums/campaign.enum';
import {
  TBriefingNoteCreatePayload,
  TBriefingNoteUpdatePayload,
  TCampaignApi,
  TCampaignCreatorCreatePayload,
  TCampaignUpdatePayload,
  TGlobalTrackingCreatePayload,
} from '@pages/campaigns/campaigns.interface';
import { ALL_CREATORS_ID } from '@shared/constants/all-creators.constants';
import { DateUtilitiesService } from '@utilities/date-utilities.service';
import { startCase } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import {
  ECampaignFormSection,
  ECampaignFormSubmitType,
  ICampaignCreatorForm,
  ICampaignForm,
  ICampaignFormSubmitInfo,
  ICtaForm,
  IGlobalTrackingForm,
  TUniqueCtaTemplates,
} from './campaign-form.interface';
import { ICreatorsAndCtasValues } from './form-fields/creators-and-ctas/creators-and-ctas.interface';
import { TCtaTemplateCreatePayload } from './form-fields/creators-and-ctas/cta-dialog.interface';
import { IChatbotMulti } from './form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/chatbot-multi/chatbot-multi.interface';
import { IChatbot } from './form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/chatbot/chatbot.interface';
import { IGamePlay } from './form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/game-play/game-play.interface';
import {
  IProductPlacement,
  IProductPlacementDeliverable,
} from './form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/product-placement/product-placement.interface';
import { IImageDeliverable } from './form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/shared/image-deliverable/image-deliverable.interface';
import { IText } from './form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/shared/text-deliverable/text-deliverable.interface';
import { ITextDescription } from './form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/shared/text-description-deliverable/text-description-deliverable.interface';
import {
  IVisual,
  IVisualDeliverable,
} from './form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/visual/visual.interface';
import { TExecutionApi } from './form-fields/creators-and-ctas/deliverable-flow-dialog/execution-step/execution-list/execution/execution.interface';

@Injectable({
  providedIn: 'root',
})
export class CampaignFormPayloadService {
  private readonly dateUtils = inject(DateUtilitiesService);

  public getCampaignUpdatePayload(
    campaign: TCampaignApi,
    basicsValues: FormGroup<ICampaignForm>['value'],
    creatorsAndCtasValues: ICreatorsAndCtasValues,
    keywordTrackingValues: FormGroup<IGlobalTrackingForm>['value'],
    submitInfo: ICampaignFormSubmitInfo,
  ): TCampaignUpdatePayload {
    const { type, section, mappedCtaTemplates } = submitInfo;
    const { briefingNotes, campaignCreatorsValue, ctasValue } = creatorsAndCtasValues;
    let campaignStatus: ECampaignStatus = campaign?.status || ECampaignStatus.draft;

    if (type === ECampaignFormSubmitType.start && campaignStatus === ECampaignStatus.draft) {
      campaignStatus = ECampaignStatus.planned;
    }

    return {
      briefingNotes: this.getBriefingNotes(campaign, briefingNotes),
      budget: basicsValues.budget ? Number(basicsValues.budget) : null,
      campaignCreators:
        campaignCreatorsValue.reduce((acc, campaignCreator) => {
          if (campaignCreator && campaignCreator?.creator?.id && campaignCreator?.creator?.id !== ALL_CREATORS_ID) {
            acc.push({
              cost: campaignCreator.cost || null,
              creatorId: campaignCreator.creator?.id!,
            });
          }

          return acc;
        }, [] as TCampaignCreatorCreatePayload[]) || [],
      ctaTemplates: (
        mappedCtaTemplates ?? this.getMappedCtaTemplates(campaign, campaignCreatorsValue, ctasValue)
      ).filter((cta) => cta.ctaCreators.length || cta.isAllCreators),
      currency: basicsValues.currency!,
      customerId: basicsValues.customer?.id || null,
      end: section === ECampaignFormSection.basics ? this.getCombinedDateString(basicsValues, 'end') : campaign?.end,
      globalTracking: this.getGlobalTracking(keywordTrackingValues),
      iconId: campaign.iconId || null,
      id: campaign?.id || '',
      name: basicsValues.name!,
      notes: basicsValues.notes || null,
      start:
        section === ECampaignFormSection.basics ? this.getCombinedDateString(basicsValues, 'start') : campaign?.start,
      status: campaignStatus,
      // Deliberately reads only `team`, not a `basicsValues.teamId` fallback - CampaignTeamSelectComponent
      // keeps `teamId` synced to `team` on every selection (see its subscribeToTeamChanges), so there's
      // no code path where `team` is null while `teamId` legitimately holds a value; adding that
      // fallback would risk resurrecting a stale/desynced teamId instead.
      teamId: basicsValues.team?.id || null,
      whitelist: basicsValues.whitelist ?? null,
      ...(section === ECampaignFormSection.keywordTracking || campaign?.section3Completed
        ? { section3Completed: true }
        : {}),
    };
  }

  public getMappedCtaTemplates(
    campaign: TCampaignApi | null,
    campaignCreatorsValue: FormGroup<ICampaignCreatorForm>['value'][],
    ctasValue: FormGroup<ICtaForm>['value'][],
  ): TCtaTemplateCreatePayload[] {
    const uniqueCtaTemplates = this.getUniqueCtaTemplates(campaignCreatorsValue, ctasValue);

    return (
      uniqueCtaTemplates?.map(
        (cta): TCtaTemplateCreatePayload => ({
          ...(campaign?.ctaTemplates?.find((ct) => ct.id === cta.id) ? { id: cta.id } : {}),
          ...this.getDeliverablesWithImages(cta),
          audioTrackings: cta.deliverables?.filter((d) => d.type === EDeliverableType.audioTracking) as IText[],
          captions: cta.deliverables?.filter((d) => d.type === EDeliverableType.caption) as IText[],
          chatbotMultis: cta.deliverables?.filter((d) => d.type === EDeliverableType.chatbotMulti) as IChatbotMulti[],
          chatbots: cta.deliverables?.filter((d) => d.type === EDeliverableType.chatBot) as IChatbot[],
          ctaCreators: cta.ctaCreators?.filter((ctaCreator) => ctaCreator.creatorId !== ALL_CREATORS_ID) || [],
          customCtaValues: cta.customCtaValues || [],
          descriptions: cta.deliverables?.filter((d) => d.type === EDeliverableType.description) as IText[],
          exclusiveTo: cta.exclusiveTo,
          executions: this.getExecutions(cta),
          gamePlays: cta.deliverables?.filter((d) => d.type === EDeliverableType.gamePlay) as IGamePlay[],
          hashTags: cta.deliverables?.filter((d) => d.type === EDeliverableType.hashTag) as IText[],
          isAllCreators: !!cta.isAllCreators,
          mentions: cta.deliverables?.filter((d) => d.type === EDeliverableType.mention) as IText[],
          messages: cta.deliverables?.filter((d) => d.type === EDeliverableType.message) as IText[],
          name: cta.name!,
          platformFeature: startCase(cta.platformFeature || '').replace(/\s+/g, '') as EPlatformFeature,
          talkingPointAis: cta.deliverables?.filter(
            (d) => d.type === EDeliverableType.talkingPointAi,
          ) as ITextDescription[],
          talkingPoints: cta.deliverables?.filter((d) => d.type === EDeliverableType.talkingPoint) as IText[],
          textTrackings: cta.deliverables?.filter((d) => d.type === EDeliverableType.textTracking) as IText[],
          titles: cta.deliverables?.filter((d) => d.type === EDeliverableType.title) as IText[],
        }),
      ) ?? []
    );
  }

  private getUniqueCtaTemplates(
    campaignCreatorsValue: FormGroup<ICampaignCreatorForm>['value'][],
    ctasValue: FormGroup<ICtaForm>['value'][],
  ): TUniqueCtaTemplates[] {
    const uniqueCtaTemplatesMap = new Map<string, TUniqueCtaTemplates>();

    campaignCreatorsValue.forEach((campaignCreator) => {
      campaignCreator?.ctas?.forEach((cta) => {
        if (!cta.id) {
          return;
        }

        const existingTemplate = uniqueCtaTemplatesMap.get(cta.id);

        if (existingTemplate) {
          existingTemplate.ctaCreators.push({
            creatorId: campaignCreator.creatorId!,
            creatorName: campaignCreator.creator?.name ?? undefined,
            ...(cta.id ? { ctaTemplateId: cta.id } : {}),
          });

          return;
        }

        uniqueCtaTemplatesMap.set(cta.id, {
          ...cta,
          ctaCreators: [
            {
              creatorId: campaignCreator.creatorId!,
              creatorName: campaignCreator.creator?.name ?? undefined,
              ...(cta.id ? { ctaTemplateId: cta.id } : {}),
            },
          ],
        });
      });
    });

    ctasValue.forEach((cta) => {
      if (!cta.id) {
        return;
      }

      const existingTemplate = uniqueCtaTemplatesMap.get(cta.id);

      if (!existingTemplate) {
        uniqueCtaTemplatesMap.set(cta.id, {
          ...cta,
          ctaCreators: [],
        });
      }
    });

    return Array.from(uniqueCtaTemplatesMap.values());
  }

  private getDeliverablesWithImages(
    cta: TUniqueCtaTemplates,
  ): Pick<TCtaTemplateCreatePayload, 'contents' | 'productPlacements' | 'showcases' | 'visuals' | 'customs'> {
    return {
      contents: cta.deliverables
        ?.filter((d) => d.type === EDeliverableType.content)
        .map((content) => ({
          ...content,
          images: (content as IImageDeliverable).images.map((image) => ({
            ...image,
            id: image.id,
            imageId: image?.imageId ?? null,
          })),
        })) as IImageDeliverable[],
      customs: cta.deliverables
        ?.filter((d) => d.type === EDeliverableType.custom)
        .map((custom) => ({
          ...custom,
          images: (custom as IImageDeliverable).images.map((image) => ({
            ...image,
            id: image.id,
            imageId: image?.imageId ?? null,
          })),
        })) as IImageDeliverable[],
      productPlacements: cta.deliverables
        ?.filter((d) => d.type === EDeliverableType.productPlacement)
        .map((productPlacement) => ({
          ...productPlacement,
          images: (productPlacement as IProductPlacementDeliverable).images.map((image) => ({
            ...image,
            id: image.id,
            imageId: image?.imageId ?? null,
          })),
        })) as IProductPlacement[],
      showcases: cta.deliverables
        ?.filter((d) => d.type === EDeliverableType.showcase)
        .map((showcase) => ({
          ...showcase,
          images: (showcase as IImageDeliverable).images.map((image) => ({
            ...image,
            id: image.id,
            imageId: image?.imageId ?? null,
          })),
        })) as IImageDeliverable[],
      visuals: cta.deliverables
        ?.filter((d) => d.type === EDeliverableType.visual)
        .map((visual) => ({
          ...visual,
          id: visual.id,
          imageId: (visual as IVisualDeliverable).imageId ?? null,
        })) as IVisual[],
    };
  }

  private getExecutions(cta: TUniqueCtaTemplates): TExecutionApi[] {
    return cta.executions?.map((execution) => ({
      ...execution,
      // duration's input is text + numberOnly (see execution.component.html), so a cleared field
      // leaves the raw form value as '' rather than null - the API expects an integer or null.
      duration: (execution.duration as unknown as string) === '' ? null : execution.duration,
      executionDate: execution.executionDate
        ? this.dateUtils.formatToApiDateTime(this.dateUtils.combineDateTime(execution.executionDate, '12:00:00'), true)
        : null,
      executionTime:
        execution.executionDate && execution.executionTime
          ? this.dateUtils.formatToApiDateTime(
              this.dateUtils.combineDateTime(execution.executionDate, execution.executionTime),
            )
          : null,
    })) as TExecutionApi[];
  }

  private getCombinedDateString(form: FormGroup<ICampaignForm>['value'], dateType: 'start' | 'end'): string {
    const combinedDate = this.getCombinedDate(form, dateType);

    return this.dateUtils.formatToApiDateTime(combinedDate) || '';
  }

  private getCombinedDate(form: FormGroup<ICampaignForm>['value'], dateType: 'start' | 'end'): Date {
    const date = form[`${dateType}Date`]!;
    const time = form[`${dateType}Time`];

    return this.dateUtils.combineDateTime(date, time);
  }

  private getGlobalTracking(
    keywordTrackingForm: FormGroup<IGlobalTrackingForm>['value'],
  ): TGlobalTrackingCreatePayload {
    return {
      ...(keywordTrackingForm?.id ? { id: keywordTrackingForm.id } : {}),
      audioTrackings:
        (keywordTrackingForm?.trackings?.filter(
          (tracking) => tracking.type === EDeliverableType.audioTracking,
        ) as IText[]) || null,
      textTrackings:
        (keywordTrackingForm?.trackings?.filter(
          (tracking) => tracking.type === EDeliverableType.textTracking,
        ) as IText[]) || null,
    };
  }

  private getBriefingNotes(
    campaign: TCampaignApi,
    briefingNotes: TCampaignApi['briefingNotes'],
  ): TBriefingNoteCreatePayload[] {
    return (
      briefingNotes?.reduce((accumulator: TBriefingNoteUpdatePayload[], briefingNote) => {
        const creatorId =
          campaign.campaignCreators?.find((campaignCreator) => campaignCreator.creatorId === briefingNote.creatorId)
            ?.creatorId ??
          briefingNote.creatorId ??
          null;
        const files =
          briefingNote.files?.map((file) => ({
            ...(file.id ? { id: file.id } : {}),
            fileId: file.fileId,
          })) ?? [];
        const hasNote = !!briefingNote.note?.trim();

        if (hasNote || files.length) {
          accumulator.push({
            creatorId,
            files,
            id: briefingNote.id || uuidv4(),
            note: briefingNote.note,
          });
        }

        return accumulator;
      }, []) || []
    );
  }
}
